class RealLifeVideoCarousel extends HTMLElement {
  constructor() {
    super();

    this.list = null;
    this.previousButton = null;
    this.nextButton = null;
    this.abortController = null;
    this.updateNavigation = this.updateNavigation.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    this.abortController?.abort();
    this.abortController = null;
  }

  init() {
    this.cleanup();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.list = this.querySelector('[data-real-life-video-carousel-list]');
    this.previousButton = this.querySelector('[data-real-life-video-carousel-prev]');
    this.nextButton = this.querySelector('[data-real-life-video-carousel-next]');

    if (!this.list) {
      return;
    }

    if (typeof window.initCarouselScroll === 'function') {
      window.initCarouselScroll(this.list, {
        draggingClass: 'real-life-video-carousel__list--dragging',
        onScroll: this.updateNavigation,
        ignoreSelector:
          'button, input, select, textarea, add-to-cart, quick-buy-button, [data-real-life-video-carousel-toggle], .real-life-video-carousel__product-title, .real-life-video-carousel__product-action, .real-life-video-carousel__shop-button',
      });
    }

    if (this.previousButton) {
      this.previousButton.addEventListener('click', () => this.scrollByCard(-1), { signal });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollByCard(1), { signal });
    }

    window.addEventListener('resize', this.updateNavigation, { signal });
    this.bindVideoEvents(signal);
    this.updateNavigation();
  }

  bindVideoEvents(signal) {
    this.querySelectorAll('[data-real-life-video-carousel-card]').forEach((card) => {
      const video = card.querySelector('.real-life-video-carousel__video');
      const toggle = card.querySelector('[data-real-life-video-carousel-toggle]');

      if (!video || !toggle) {
        if (toggle) {
          toggle.disabled = true;
        }
        return;
      }

      toggle.addEventListener('click', () => this.toggleVideo(card, video), { signal });
      video.addEventListener('play', () => this.setCardPlaying(card, true), { signal });
      video.addEventListener('pause', () => this.setCardPlaying(card, false), { signal });
    });
  }

  toggleVideo(card, video) {
    if (video.paused) {
      this.pauseOtherVideos(video);
      video.muted = false;
      video.volume = 1;
      video.play().catch(() => {
        this.setCardPlaying(card, false);
      });
      return;
    }

    video.pause();
  }

  pauseOtherVideos(activeVideo) {
    this.querySelectorAll('.real-life-video-carousel__video').forEach((video) => {
      if (video !== activeVideo && !video.paused) {
        video.pause();
      }
    });
  }

  setCardPlaying(card, isPlaying) {
    card.classList.toggle('real-life-video-carousel__card--playing', isPlaying);

    const toggle = card.querySelector('[data-real-life-video-carousel-toggle]');

    if (toggle) {
      toggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }
  }

  scrollByCard(direction) {
    const firstItem = this.list.querySelector('.real-life-video-carousel__item');

    if (!firstItem) {
      return;
    }

    const gap = Number.parseFloat(window.getComputedStyle(this.list).columnGap) || 0;
    this.list.scrollBy({
      left: direction * (firstItem.getBoundingClientRect().width + gap),
      behavior: 'smooth',
    });

    window.setTimeout(() => this.updateNavigation(), 350);
  }

  updateNavigation() {
    if (!this.list || !this.previousButton || !this.nextButton) {
      return;
    }

    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const isScrollable = maxScroll > 1;

    this.previousButton.hidden = !isScrollable;
    this.nextButton.hidden = !isScrollable;
    this.previousButton.disabled = this.list.scrollLeft <= 1;
    this.nextButton.disabled = this.list.scrollLeft >= maxScroll - 1;
  }
}

if (!customElements.get('real-life-video-carousel')) {
  customElements.define('real-life-video-carousel', RealLifeVideoCarousel);

  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('real-life-video-carousel');

    if (carousel) {
      carousel.init();
    }
  });
}
